import React, { useState, useMemo } from 'react';
import { Check, ChevronRight, Upload, Map, Settings, BarChart3, CheckCircle } from 'lucide-react';
import { FileUploader } from './FileUploader';
import { ColumnMapper } from './ColumnMapper';
import { DuplicateSettings } from './DuplicateSettings';
import { ImportProgress } from './ImportProgress';
import { ImportErrorTable } from './ImportErrorTable';
import { useLeadImport } from '@/hooks/useLeadImport';
import { ImportPreviewResponse, ImportJob, LEAD_FIELDS } from '@/types/import';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ImportWizardProps {
  onComplete: () => void;
  onCancel: () => void;
}

type WizardStep = 'upload' | 'mapping' | 'settings' | 'progress' | 'complete';

const steps = [
  { id: 'upload', label: 'Upload', icon: Upload },
  { id: 'mapping', label: 'Map Fields', icon: Map },
  { id: 'settings', label: 'Settings', icon: Settings },
  { id: 'progress', label: 'Import', icon: BarChart3 },
  { id: 'complete', label: 'Complete', icon: CheckCircle },
];

export const ImportWizard: React.FC<ImportWizardProps> = ({
  onComplete,
  onCancel,
}) => {
  const {
    uploadFile,
    startImport,
    cancelImport,
    getJobStatus,
    getJobErrors,
    isUploading,
    error: uploadError,
  } = useLeadImport();

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>('upload');
  const [preview, setPreview] = useState<ImportPreviewResponse | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [completedJob, setCompletedJob] = useState<ImportJob | null>(null);

  // Form state
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [duplicateMode, setDuplicateMode] = useState<'SKIP' | 'UPDATE' | 'CREATE_NEW'>('SKIP');
  const [defaultAssignee, setDefaultAssignee] = useState<string>('');
  const [defaultPriority, setDefaultPriority] = useState<string>('');
  const [defaultStage, setDefaultStage] = useState<string>('');

  // Current step index
  const currentStepIndex = steps.findIndex(s => s.id === currentStep);

  // Handle file upload
  const handleFileSelect = async (file: File) => {
    try {
      const result = await uploadFile(file);
      setPreview(result);

      // Initialize field mapping with suggested mappings
      const initialMapping: Record<string, string> = {};
      result.headers.forEach(header => {
        const suggestion = result.suggestedMapping.find(s => s.csvColumn === header);
        initialMapping[header] = suggestion?.leadField || '';
      });
      setFieldMapping(initialMapping);

      setCurrentStep('mapping');
    } catch (err) {
      // Error is handled by the hook
    }
  };

  // Handle mapping change
  const handleMappingChange = (csvColumn: string, leadField: string) => {
    setFieldMapping(prev => ({
      ...prev,
      [csvColumn]: leadField,
    }));
  };

  // Validate mapping
  const isMappingValid = useMemo(() => {
    const requiredFields = LEAD_FIELDS.filter(f => f.required).map(f => f.value);
    const mappedFields = Object.values(fieldMapping);
    return requiredFields.every(field => mappedFields.includes(field));
  }, [fieldMapping]);

  // Start import
  const handleStartImport = async () => {
    if (!preview) return;

    try {
      const result = await startImport({
        importJobId: preview.importJobId,
        fieldMapping,
        duplicateMode,
        defaultValues: {
          assignedToId: defaultAssignee || undefined,
          priority: defaultPriority || undefined,
          stage: defaultStage || undefined,
        },
      });

      setJobId(result.jobId);
      setCurrentStep('progress');
    } catch (err) {
      // Error is handled by the hook
    }
  };

  // Handle import complete
  const handleImportComplete = (job: ImportJob) => {
    setCompletedJob(job);
    setCurrentStep('complete');
  };

  // Handle import cancel
  const handleCancelImport = async () => {
    if (jobId) {
      await cancelImport(jobId);
    }
    onCancel();
  };

  // Navigation
  const goBack = () => {
    switch (currentStep) {
      case 'mapping':
        setCurrentStep('upload');
        setPreview(null);
        break;
      case 'settings':
        setCurrentStep('mapping');
        break;
      default:
        break;
    }
  };

  const goNext = () => {
    switch (currentStep) {
      case 'mapping':
        setCurrentStep('settings');
        break;
      case 'settings':
        handleStartImport();
        break;
      default:
        break;
    }
  };

  return (
    <div className="space-y-6">
      {/* Step indicator */}
      <div className="flex items-center justify-between mb-8">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isCompleted = index < currentStepIndex;
          const isCurrent = step.id === currentStep;
          const isUpcoming = index > currentStepIndex;

          return (
            <React.Fragment key={step.id}>
              <div className="flex items-center">
                <div className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center transition-colors',
                  isCompleted ? 'bg-green-100 text-green-600' :
                  isCurrent ? 'bg-blue-100 text-blue-600' :
                  'bg-gray-100 text-gray-400'
                )}>
                  {isCompleted ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Icon className="w-5 h-5" />
                  )}
                </div>
                <span className={cn(
                  'ml-2 text-sm font-medium hidden sm:block',
                  isCurrent ? 'text-gray-900' : 'text-gray-500'
                )}>
                  {step.label}
                </span>
              </div>

              {index < steps.length - 1 && (
                <ChevronRight className={cn(
                  'w-5 h-5 mx-2',
                  index < currentStepIndex ? 'text-green-400' : 'text-gray-300'
                )} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step content */}
      <div className="min-h-[400px]">
        {currentStep === 'upload' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Upload Your File</h3>
              <p className="text-sm text-gray-500">
                Upload a CSV or Excel file containing your leads. We'll help you map the columns to the correct fields.
              </p>
            </div>

            <FileUploader
              onFileSelect={handleFileSelect}
              isLoading={isUploading}
              error={uploadError}
            />
          </div>
        )}

        {currentStep === 'mapping' && preview && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Map Columns to Lead Fields</h3>
              <p className="text-sm text-gray-500">
                Match each column in your file to the corresponding lead field. Required fields are marked with *.
              </p>
            </div>

            <ColumnMapper
              csvColumns={preview.headers}
              sampleData={preview.sampleRows}
              suggestedMapping={preview.suggestedMapping}
              fieldMapping={fieldMapping}
              onMappingChange={handleMappingChange}
            />
          </div>
        )}

        {currentStep === 'settings' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Import Settings</h3>
              <p className="text-sm text-gray-500">
                Configure how duplicates should be handled and set default values for missing fields.
              </p>
            </div>

            <DuplicateSettings
              duplicateMode={duplicateMode}
              onDuplicateModeChange={setDuplicateMode}
              defaultAssignee={defaultAssignee}
              onAssigneeChange={setDefaultAssignee}
              defaultPriority={defaultPriority}
              onPriorityChange={setDefaultPriority}
              defaultStage={defaultStage}
              onStageChange={setDefaultStage}
            />
          </div>
        )}

        {currentStep === 'progress' && jobId && (
          <ImportProgress
            jobId={jobId}
            onComplete={handleImportComplete}
            onCancel={handleCancelImport}
            getJobStatus={getJobStatus}
          />
        )}

        {currentStep === 'complete' && completedJob && (
          <div className="space-y-6">
            <div className="text-center py-6">
              {completedJob.status === 'COMPLETED' ? (
                <>
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 mb-2">Import Complete!</h3>
                  <p className="text-sm text-gray-500">
                    Successfully imported {completedJob.successCount.toLocaleString()} leads.
                  </p>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 mb-2">Import {completedJob.status}</h3>
                  <p className="text-sm text-gray-500">
                    {completedJob.errorMessage || 'The import process has ended.'}
                  </p>
                </>
              )}
            </div>

            {/* Summary stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{completedJob.successCount.toLocaleString()}</div>
                <div className="text-sm text-green-700">Imported</div>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-red-600">{completedJob.failedCount.toLocaleString()}</div>
                <div className="text-sm text-red-700">Failed</div>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-yellow-600">{completedJob.skippedCount.toLocaleString()}</div>
                <div className="text-sm text-yellow-700">Skipped</div>
              </div>
            </div>

            {/* Errors */}
            {completedJob.failedCount > 0 && (
              <ImportErrorTable
                jobId={completedJob.id}
                totalErrors={completedJob.failedCount}
                getJobErrors={getJobErrors}
              />
            )}
          </div>
        )}
      </div>

      {/* Navigation buttons */}
      <div className="flex items-center justify-between pt-4 border-t">
        <div>
          {['mapping', 'settings'].includes(currentStep) && (
            <Button variant="outline" onClick={goBack}>
              Back
            </Button>
          )}
        </div>

        <div className="flex items-center space-x-3">
          {currentStep !== 'progress' && (
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          )}

          {currentStep === 'mapping' && (
            <Button
              onClick={goNext}
              disabled={!isMappingValid}
            >
              Continue
            </Button>
          )}

          {currentStep === 'settings' && (
            <Button onClick={goNext}>
              Start Import
            </Button>
          )}

          {currentStep === 'complete' && (
            <Button onClick={onComplete}>
              Done
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
